import { Application, Graphics, Container, Text, TextStyle } from 'pixi.js';

export interface InputState {
  /** Normalized movement direction (-1 to 1) */
  dx: number;
  dy: number;
  /** Whether player is actively providing input */
  moving: boolean;
}

/**
 * Handles player input: WASD keyboard, click/tap-to-move, virtual joystick (mobile).
 * Exposes a normalized movement vector each frame.
 */
export class InputManager {
  private app: Application;

  // Keyboard state
  private keys = new Map<string, boolean>();

  // Click-to-move
  private clickTarget: { x: number; y: number } | null = null;
  private playerWorldPos: { x: number; y: number } = { x: 0, y: 0 };

  // Virtual joystick
  private joystickContainer: Container;
  private joystickBg: Graphics;
  private joystickKnob: Graphics;
  private joystickActive = false;
  private joystickOrigin = { x: 0, y: 0 };
  private joystickDelta = { x: 0, y: 0 };
  private joystickRadius = 50;
  private joystickTouchId: number | null = null;

  // State
  private _state: InputState = { dx: 0, dy: 0, moving: false };

  constructor(app: Application) {
    this.app = app;

    // Keyboard listeners
    const onKeyDown = (e: KeyboardEvent) => {
      this.keys.set(e.key.toLowerCase(), true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      this.keys.set(e.key.toLowerCase(), false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    // Build virtual joystick (initially hidden, shows on touch)
    this.joystickContainer = new Container();
    this.joystickContainer.visible = false;
    this.joystickContainer.zIndex = 1000;

    this.joystickBg = new Graphics();
    this.joystickBg.circle(0, 0, this.joystickRadius);
    this.joystickBg.fill({ color: 0x000000, alpha: 0.25 });
    this.joystickBg.stroke({ color: 0xffffff, width: 2, alpha: 0.3 });
    this.joystickContainer.addChild(this.joystickBg);

    this.joystickKnob = new Graphics();
    this.joystickKnob.circle(0, 0, 20);
    this.joystickKnob.fill({ color: 0xffffff, alpha: 0.5 });
    this.joystickContainer.addChild(this.joystickKnob);
  }

  /** Returns the joystick container to be added to the UI layer */
  getJoystickContainer(): Container {
    return this.joystickContainer;
  }

  /**
   * Setup click-to-move on a given interactive area.
   * `worldContainer` is the scrollable world, so we can convert screen→world coords.
   */
  setupClickToMove(hitArea: Graphics, worldContainer: Container) {
    hitArea.eventMode = 'static';
    hitArea.on('pointerdown', (e) => {
      // Ignore if joystick is active (mobile touch)
      if (this.joystickActive) return;

      const local = worldContainer.toLocal(e.global);
      this.clickTarget = { x: local.x, y: local.y };
    });
  }

  /**
   * Setup touch joystick on a given hit area (usually the full screen).
   * The joystick appears where the user touches on the left half of the screen.
   */
  setupTouchJoystick(hitArea: Graphics) {
    hitArea.eventMode = 'static';

    hitArea.on('pointerdown', (e) => {
      const W = this.app.screen.width;
      // Only activate on left half of screen
      if (e.global.x > W * 0.5) return;
      // Only for touch (not mouse)
      if (e.pointerType !== 'touch') return;

      this.joystickActive = true;
      this.joystickTouchId = e.pointerId;
      this.joystickOrigin.x = e.global.x;
      this.joystickOrigin.y = e.global.y;
      this.joystickContainer.x = e.global.x;
      this.joystickContainer.y = e.global.y;
      this.joystickContainer.visible = true;
      this.joystickKnob.x = 0;
      this.joystickKnob.y = 0;
      this.joystickDelta = { x: 0, y: 0 };
    });

    hitArea.on('pointermove', (e) => {
      if (!this.joystickActive) return;
      if (e.pointerId !== this.joystickTouchId) return;

      const dx = e.global.x - this.joystickOrigin.x;
      const dy = e.global.y - this.joystickOrigin.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const clamped = Math.min(dist, this.joystickRadius);
      const angle = Math.atan2(dy, dx);

      this.joystickKnob.x = Math.cos(angle) * clamped;
      this.joystickKnob.y = Math.sin(angle) * clamped;
      this.joystickDelta.x = (clamped / this.joystickRadius) * Math.cos(angle);
      this.joystickDelta.y = (clamped / this.joystickRadius) * Math.sin(angle);
    });

    const endJoystick = (e: any) => {
      if (!this.joystickActive) return;
      if (e.pointerId !== this.joystickTouchId) return;

      this.joystickActive = false;
      this.joystickTouchId = null;
      this.joystickContainer.visible = false;
      this.joystickDelta = { x: 0, y: 0 };
    };

    hitArea.on('pointerup', endJoystick);
    hitArea.on('pointerupoutside', endJoystick);
    hitArea.on('pointercancel', endJoystick);
  }

  /** Call each frame to update input state */
  update(playerWorldX: number, playerWorldY: number) {
    this.playerWorldPos.x = playerWorldX;
    this.playerWorldPos.y = playerWorldY;

    let dx = 0;
    let dy = 0;

    // Priority 1: Virtual joystick
    if (this.joystickActive) {
      dx = this.joystickDelta.x;
      dy = this.joystickDelta.y;
      this.clickTarget = null; // Cancel click-to-move
    }
    // Priority 2: Keyboard WASD / Arrow keys
    else if (this.isKeyboardMoving()) {
      if (this.keys.get('w') || this.keys.get('arrowup')) dy -= 1;
      if (this.keys.get('s') || this.keys.get('arrowdown')) dy += 1;
      if (this.keys.get('a') || this.keys.get('arrowleft')) dx -= 1;
      if (this.keys.get('d') || this.keys.get('arrowright')) dx += 1;

      // Normalize diagonal
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 0) {
        dx /= len;
        dy /= len;
      }
      this.clickTarget = null; // Cancel click-to-move
    }
    // Priority 3: Click-to-move
    else if (this.clickTarget) {
      const tdx = this.clickTarget.x - playerWorldX;
      const tdy = this.clickTarget.y - playerWorldY;
      const dist = Math.sqrt(tdx * tdx + tdy * tdy);

      if (dist < 8) {
        // Arrived
        this.clickTarget = null;
      } else {
        dx = tdx / dist;
        dy = tdy / dist;
      }
    }

    const moving = Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01;
    this._state = { dx, dy, moving };
  }

  /** Get current input state */
  getState(): InputState {
    return this._state;
  }

  /** Cancel any active click-to-move target */
  cancelClickTarget() {
    this.clickTarget = null;
  }

  private isKeyboardMoving(): boolean {
    return !!(
      this.keys.get('w') || this.keys.get('a') || this.keys.get('s') || this.keys.get('d') ||
      this.keys.get('arrowup') || this.keys.get('arrowleft') || this.keys.get('arrowdown') || this.keys.get('arrowright')
    );
  }

  destroy() {
    // Cleanup would go here if needed
  }
}
